// src/contexts/ThemeContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';

// Define your themes
const themes = {
  // 🌙 DARK THEME (CarbonX style - deep blue/black with green accents)
  dark: {
    name: 'Dark',
    primary: '#0A0F1E',
    secondary: '#0D1425',
    surface: '#111827',
    
    // Accent colors
    accent1: '#22C55E', // vibrant green
    accent2: '#3B82F6', // blue
    accent3: '#8B5CF6', // purple
    
    hover: '#1F2937',
    background: '#0A0F1E',
    
    text: '#FFFFFF',
    textSecondary: '#9CA3AF',
    border: '#1F2937',
    
    success: '#22C55E',
    error: '#EF4444',
    warning: '#F59E0B',
    info: '#3B82F6',
    
    // Card and component specific colors
    cardBg: '#0D1425',
    cardBorder: '#1F2937',
    inputBg: '#111827',
    
    gradient: {
      primary: 'linear-gradient(135deg, #0A0F1E 0%, #111827 100%)',
      accent: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
      hover: 'linear-gradient(135deg, #1F2937 0%, #111827 100%)'
    }
  },
  
  // ☀️ LIGHT THEME (Clean white/light gray with green accents)
  light: {
    name: 'Light',
    primary: '#FFFFFF',
    secondary: '#F8FAFC',
    surface: '#FFFFFF',
    
    // Accent colors
    accent1: '#16A34A', // forest green
    accent2: '#3B82F6', // blue
    accent3: '#8B5CF6', // purple
    
    hover: '#F1F5F9',
    background: '#F8FAFC',
    
    text: '#0F172A',
    textSecondary: '#475569',
    border: '#E2E8F0',
    
    success: '#16A34A',
    error: '#EF4444',
    warning: '#F59E0B',
    info: '#3B82F6',
    
    // Card and component specific colors
    cardBg: '#FFFFFF',
    cardBorder: '#E2E8F0',
    inputBg: '#FFFFFF',
    
    gradient: {
      primary: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
      accent: 'linear-gradient(135deg, #16A34A 0%, #22C55E 100%)',
      hover: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)'
    }
  },
  
  // ✨ PRESENT THEME (Modern dark theme - cyan/indigo with glass morphism effect)
  present: {
    name: 'Present',
    primary: '#0B1120',
    secondary: '#0F172A',
    surface: '#1E293B',
    
    // Accent colors - cyan and indigo for modern tech look
    accent1: '#06B6D4', // cyan
    accent2: '#6366F1', // indigo
    accent3: '#22C55E', // green accent
    
    hover: '#334155',
    background: '#0B1120',
    
    text: '#F1F5F9',
    textSecondary: '#94A3B8',
    border: '#1E293B',
    
    success: '#22C55E',
    error: '#EF4444',
    warning: '#F59E0B',
    info: '#06B6D4',
    
    // Card and component specific colors - more vibrant than dark theme
    cardBg: '#0F172A',
    cardBorder: '#1E293B',
    inputBg: '#1E293B',
    
    gradient: {
      primary: 'linear-gradient(135deg, #0B1120 0%, #0F172A 100%)',
      accent: 'linear-gradient(135deg, #06B6D4 0%, #6366F1 100%)',
      hover: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)'
    }
  },
  
  // 🎨 OCEAN THEME (Deep ocean blues with teal accents)
  ocean: {
    name: 'Ocean',
    primary: '#0F172A',
    secondary: '#1E2A4F',
    surface: '#162241',
    
    // Accent colors - teal and ocean blue
    accent1: '#14B8A6', // teal
    accent2: '#0EA5E9', // sky blue
    accent3: '#8B5CF6', // purple
    
    hover: '#1E3A5F',
    background: '#0F172A',
    
    text: '#F0F9FF',
    textSecondary: '#94A3B8',
    border: '#1E3A5F',
    
    success: '#14B8A6',
    error: '#EF4444',
    warning: '#F59E0B',
    info: '#0EA5E9',
    
    cardBg: '#162241',
    cardBorder: '#1E3A5F',
    inputBg: '#1E2A4F',
    
    gradient: {
      primary: 'linear-gradient(135deg, #0F172A 0%, #1E2A4F 100%)',
      accent: 'linear-gradient(135deg, #14B8A6 0%, #0EA5E9 100%)',
      hover: 'linear-gradient(135deg, #1E3A5F 0%, #162241 100%)'
    }
  },
  
  // 🌿 GREEN THEME (Nature-inspired dark green theme)
  green: {
    name: 'Green',
    primary: '#0D1F14',
    secondary: '#1A2E1F',
    surface: '#162B1C',
    
    // Accent colors - nature greens
    accent1: '#4ADE80', // light green
    accent2: '#22C55E', // green
    accent3: '#A3E635', // lime
    
    hover: '#2D3E2A',
    background: '#0D1F14',
    
    text: '#ECFDF5',
    textSecondary: '#A7F3D0',
    border: '#2D3E2A',
    
    success: '#4ADE80',
    error: '#EF4444',
    warning: '#FBBF24',
    info: '#22C55E',
    
    cardBg: '#162B1C',
    cardBorder: '#2D3E2A',
    inputBg: '#1A2E1F',
    
    gradient: {
      primary: 'linear-gradient(135deg, #0D1F14 0%, #1A2E1F 100%)',
      accent: 'linear-gradient(135deg, #4ADE80 0%, #22C55E 100%)',
      hover: 'linear-gradient(135deg, #2D3E2A 0%, #1A2E1F 100%)'
    }
  }
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get saved theme from localStorage or default to 'dark'
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('app-theme');
    return savedTheme && themes[savedTheme] ? savedTheme : 'dark';
  });

  // Get theme object
  const theme = themes[currentTheme];

  // Change theme
  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('app-theme', themeName);
      
      // Apply theme CSS variables to root
      applyThemeVariables(themes[themeName]);
    }
  };

  // Apply theme variables to document root
  const applyThemeVariables = (theme) => {
    const root = document.documentElement;
    Object.entries(theme).forEach(([key, value]) => {
      if (typeof value === 'object') {
        Object.entries(value).forEach(([subKey, subValue]) => {
          root.style.setProperty(`--${key}-${subKey}`, subValue);
        });
      } else {
        root.style.setProperty(`--${key}`, value);
      }
    });
  };

  // Apply theme on mount
  useEffect(() => {
    applyThemeVariables(theme);
  }, [theme]);

  const value = {
    theme,
    currentTheme,
    setTheme,
    themes: Object.keys(themes).map(key => ({
      id: key,
      name: themes[key].name,
      colors: themes[key]
    }))
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};